import { ModalButton, IModalProvider } from './../modal/model/interfaces';
import { ISocketClient } from './../socket/interfaces';
import Codes from '../socket/codes';
import { emojiFromSimilar, emojiTemplate, emojiToSimilar } from './emoji';
import { Messages } from './messages';
import { Message } from './message';
import { MessagesRepository } from './messagesRepository';
import { BasePreferences, IPreferencesService } from '../preferences/model/types';
import { BehaviorSubject } from 'rxjs';
import { IPlayer, IPlayersOnlineRepository } from '../player/model/interfaces';
import Utils from '../socket/utils';
import { CreateMessageType, ITable, ReceiveMessageType } from '@base/features/game/model/interfaces';
import BufferReader from '../socket/bufferReader';
import BaseTablesRepository from '@base/features/game/model/baseTableRepository';
import BaseTable from '../game/model/baseTable';

export class MessagesService<P extends BasePreferences = BasePreferences, T extends ITable = BaseTable> {
  onPrivateChatJoined = new BehaviorSubject<IPlayer | false>(false);

  constructor(
    private gameId: number,
    private playersOnlineRepository: IPlayersOnlineRepository<P, T>,
    private tablesRepository: BaseTablesRepository<T>,
    private preferencesService: IPreferencesService<P>,
    private messagesRepository: MessagesRepository,
    private messages: Messages,
    private socket: ISocketClient,
    private modalProvider: IModalProvider
  ) {}

  private onReceiveMessage(data: BufferReader) {
    const message: ReceiveMessageType = {
      type: data.readInt8(),
      pid: data.readInt16(),
      tid: data.readInt16(),
      text: data.readUTFWithLength(),
      color: data.readUTFWithLength(),
    };
    const sender = this.playersOnlineRepository.getPlayerOnline(message.pid);
    const myTable = this.playersOnlineRepository.me?.getTable();

    switch (message.type) {
      // Сообщения в общий чат
      case Codes.MessageTypes.ALL:
        if (!sender) break;
        if (sender.isMuted()) break;
        this.createChatMessage(message);
        break;
      // От администратора
      case Codes.MessageTypes.GOD:
        if (this.gameId !== 9) {
          const pos = message.text.indexOf('TIP OF THE DAY:');
          if (pos !== -1) message.text = message.text.substring(0, pos - 1);
        }
        this.createChatMessage(message);
        if (myTable) {
          message.tid = myTable.tid;
          this.createChatMessage(message);
        }
        break;
      // Информационные сообщения на игровой стол
      case Codes.MessageTypes.INFO:
        if (myTable) {
          message.tid = myTable.tid;
          this.createChatMessage(message);
        }
        break;
      // Индивидуальные сообщения
      case Codes.MessageTypes.INDIV: {
        if (!sender) break;
        if (sender.isMuted()) break;
        if (
          this.playersOnlineRepository.me?.getTable() &&
          this.preferencesService.getPreferences('DISALLOW_CHAT_WHILE_PLAYING')
        )
          break;
        // Для тикетных игроков не показывать в приватном чате сообщения от не тикетных игроков
        if (this.playersOnlineRepository.me?.isTicketPlayer() && !sender.isTicketPlayer()) break;
        this.createChatMessage(message);

        //Дублируем личку в разные чаты:
        //Добавляем получателя, чтобы отличать личные сообщения в main chat и table chat
        //Дублируем личку на столы (((
        if (myTable) {
          const newMessage: CreateMessageType = { ...message };
          newMessage.recipientName = this.playersOnlineRepository.me?.getName();
          newMessage.tid = myTable.tid;
          newMessage.type = Codes.MessageTypes.TABLE;
          this.createChatMessage(newMessage);
        }
        //Дублируем личные сообщения в main chat
        const newMessage: CreateMessageType = { ...message };
        newMessage.recipientName = this.playersOnlineRepository.me?.getName();
        newMessage.type = Codes.MessageTypes.ALL;
        this.createChatMessage(newMessage);
        break;
      }
      // Эхо
      case Codes.MessageTypes.YOU:
        this.handleEchoChatMessage(message);
        break;
      // Сообщения на игровой стол
      case Codes.MessageTypes.TABLE:
        if (!sender) return;
        if (sender.isMuted()) return;
        if (message.tid) this.createChatMessage(message);
        break;
      default:
        return;
    }
  }

  private send(mode: number, text: string, tid: number): void {
    const data = [Codes.Types.CP_MESSAGE]
      .concat(Utils.decToInt16(mode))
      .concat(Utils.decToInt16(tid))
      .concat(Utils.UTFtoArray(text, true, 2));
    this.socket.sendPacket(data);
  }

  showRegularWinMessage(buttons: ModalButton[], body: string, title: string, showBuyTicketsButton: boolean) {
    const modal = { title, body, buttons };
    this.modalProvider.show(modal);
  }

  showTicketWinMessage(buttons: ModalButton[], body: string, title: string) {
    // TODO sounds
    // this.sound.play(this.sound.codes.COIN, this.sound.categories.GLOBAL);
    this.playWinnerAnimation();
    const modal = { buttons, body, title };
    this.modalProvider.show(modal);
  }

  showFreeWinMessage(buttons: ModalButton[], body: string, title: string) {
    const modal = { title, body, buttons };
    this.modalProvider.show(modal);
  }

  canSend(player?: IPlayer): boolean {
    return this.messages.canSend(player);
  }

  sendMessage(message: string): void {
    if (!this.canSend(this.playersOnlineRepository.me)) return;

    const text = emojiToSimilar(message);
    const type = this.messages.isCommand(message) ? Codes.MessageTypes.COMMAND : Codes.MessageTypes.ALL;
    const tid = -1;
    this.messages.waitEcho(text, tid);
    this.send(type, text, tid);
  }

  sendMessageOnTable(message: string, tid: number): void {
    if (!this.canSend(this.playersOnlineRepository.me)) return;

    const text = emojiToSimilar(message);
    const type = this.messages.isCommand(message) ? Codes.MessageTypes.COMMAND : Codes.MessageTypes.TABLE;
    this.messages.waitEcho(text, tid);
    this.send(type, text, tid);
  }

  sendMessagePrivate(message: string, tid: number, recipientId: number): void {
    if (!this.canSend(this.playersOnlineRepository.me)) return;

    const text = emojiToSimilar(message);
    this.messages.waitEcho(text, tid, recipientId);
    this.send(recipientId, text, tid); //вместо type отдаем id получателя
  }

  handleEchoChatMessage(message: ReceiveMessageType) {
    const newMsg = new Message(message);
    newMsg.pid = this.playersOnlineRepository.me && this.playersOnlineRepository.me?.id;
    if (!this.messages.handleEcho(newMsg)) return;

    newMsg.echo = true;
    if (newMsg.recipientTableId >= 0) {
      const tid = this.playersOnlineRepository.me?.getTable()?.tid;
      if (tid) {
        //установил отправку только в чат
        newMsg.type = Codes.MessageTypes.TABLE;
        newMsg.tid = tid;
        this.addChatMessage(newMsg);
      }
    } else if (newMsg.recipientId >= 0) {
      //установил отправку только в личку
      newMsg.type = Codes.MessageTypes.INDIV;
      this.addChatMessage(newMsg);
    } else {
      this.addChatMessage(newMsg);
    }
  }

  createChatMessage(data: CreateMessageType): void {
    const newMsg = new Message(data);
    this.addChatMessage(newMsg);
  }

  private addChatMessage(msg: Message): void {
    let text = msg.text;

    text = emojiFromSimilar(text);

    const emojiReplace = new RegExp(emojiTemplate(false), 'ig');
    msg.text = text.replace(emojiReplace, '$1');

    if (msg.type === Codes.MessageTypes.TABLE && msg.tid) {
      //Отправитель
      const senderPid = msg.pid;
      if (senderPid && senderPid >= 0) {
        const sender = this.playersOnlineRepository.getPlayerOnline(senderPid);
        if (sender) {
          msg.senderName = sender.getName();
          msg.moneyPlayer = sender.isTicketPlayer();
          msg.muted = sender.isMuted();
        }
      }
      this.tablesRepository.addMessageOnTable(msg);
    }
    //В личку пришло
    else if (msg.type === Codes.MessageTypes.INDIV) {
      //отправитель
      const senderPid = msg.pid;
      if (senderPid && senderPid >= 0) {
        const sender = this.playersOnlineRepository.getPlayerOnline(senderPid);
        if (sender) {
          //msg.playerName = sender.name;
          msg.senderName = sender.getName();
          msg.moneyPlayer = sender.isTicketPlayer();
          msg.muted = sender.isMuted();

          //Получатель
          const recipientPid = msg.recipientId;
          if (recipientPid && recipientPid >= 0) {
            const recipient = this.playersOnlineRepository.getPlayerOnline(recipientPid);
            if (recipient) {
              msg.recipientName = recipient.getName();
            }
            this.playersOnlineRepository.addMessage(recipientPid, msg);
          }
        }
        this.playersOnlineRepository.addMessage(senderPid, msg);
      }
    } else {
      //в общий чат пришло
      //Отправитель
      const senderPid = msg.pid;
      if (senderPid && senderPid >= 0) {
        const sender = this.playersOnlineRepository.getPlayerOnline(senderPid);
        if (sender) {
          msg.senderName = sender.getName();
          msg.moneyPlayer = sender.isTicketPlayer();
          msg.muted = sender.isMuted();
          msg.color = this.setColor(sender);
        }
      }
      this.messagesRepository.onMessageLobby(msg);
      // Если системное оповещение, то дублируем на стол
      if (msg.type === Codes.MessageTypes.INFO) {
        const table = this.playersOnlineRepository.me?.getTable();
        table?.addChatMessage(msg);
      }
    }
  }

  setColor(player: IPlayer) {
    if (player.isAdmin()) {
      return '#FF9595';
    }
    if (player.isTD()) {
      return '#5AFF5D';
    }
    return '#ffffff';
  }

  broadcastChatMessage(message: string) {
    const newMsg = new Message({
      type: Codes.MessageTypes.GOD,
      text: message,
    });
    const table = this.playersOnlineRepository.me?.getTable();
    //отправляем на стол
    table?.addChatMessage(newMsg);
    //отправляем в общий чат
    this.messagesRepository.onMessageLobby(newMsg);
  }

  joinPrivatChat(player: IPlayer): void {
    this.onPrivateChatJoined.next(player);
  }

  cleanMessagesLobby(): void {
    this.messagesRepository.cleanMessagesLobby();
  }

  closePrivateChat(): void {
    this.onPrivateChatJoined.next(false);
  }

  playWinnerAnimation(): void {
    const exists = document.getElementById('winnerAnimation');
    if (exists) {
      exists.parentNode?.removeChild(exists);
      return;
    }

    const element = document.querySelector('body');
    if (!element) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let focused = false;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    canvas.id = 'winnerAnimation';

    const coin = new Image();
    coin.src = 'assets/images/game/coin_sprite.png';
    // 720 wide, 96 high, 90 width, 8 states
    coin.onload = function () {
      element.appendChild(canvas);
      focused = true;
      drawloop();
    };
    const coins: any = [];

    function drawloop() {
      if (!ctx) return;

      if (focused) {
        requestAnimationFrame(drawloop);
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (Math.random() < 0.3) {
        coins.push({
          x: (Math.random() * canvas.width) | 0,
          y: -50,
          dy: 3,
          s: 0.5 + Math.random(),
          state: (Math.random() * 8) | 0,
        });
      }
      let i = coins.length;
      while (i--) {
        const x = coins[i].x;
        const y = coins[i].y;
        const s = coins[i].s;
        const state = coins[i].state;
        coins[i].state = state > 7 ? 0 : state + 0.1;
        coins[i].dy += 0.3;
        coins[i].y += coins[i].dy;

        ctx.drawImage(coin, 90 * Math.floor(state), 0, 90, 96, x, y, 90 * s, 96 * s);

        if (y > canvas.height) {
          coins.splice(i, 1);
        }
      }
    }
  }
}
